import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowRight,
  Bold,
  Clock,
  Megaphone,
  MessageSquarePlus,
  NotebookPen,
  PictureInPicture2,
  SquareKanban,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { useLocale } from "@/hooks/useLocale";
import { selectUser } from "@/store/slices/user.slice";
import {
  useGetChatCapabilitiesQuery,
  useGetChatConversationsInfiniteQuery,
  useGetChatUnreadSummaryQuery,
  useOpenChatDirectMutation,
} from "@/services/(my-space)/api.chat";
import { chatHub } from "@/services/websocket/chat-hub";
import type { IChatConversation } from "@/types/(my-space)/chat.interface";
import { cn } from "@/utils/cn";
import { getErrorMessage } from "@/utils/error.utils";
import { ChatAvatar } from "./ChatAvatar";
import type { NewChatStart } from "./NewChatDialogs";
import { conversationTime, conversationTitle, lastMessagePreview } from "../_lib/chat-labels";
import { dayPart } from "../_lib/chat-utils";
import { channelOf } from "../_lib/channel-notices";

/** How many chats the "pick up" grid shows. */
const PICK_UP_COUNT = 4;

/**
 * The Chat page with nothing open: who's waiting on me (unread first, else
 * the latest chats), one-click ways to start something, and tips for what
 * chat can do — instead of an empty "select a conversation".
 */
export const ChatLanding = ({
  onOpen,
  onNew,
}: {
  onOpen: (id: number) => void;
  onNew: (start: NewChatStart) => void;
}) => {
  const { t } = useTranslation("chat");
  const me = useAppSelector(selectUser).user;
  const unread = useGetChatUnreadSummaryQuery(undefined, { skip: !me }).data;
  const canBroadcast = !!useGetChatCapabilitiesQuery().data?.canBroadcast;
  const unreadQuery = useGetChatConversationsInfiniteQuery({ tab: "unread", archived: false, search: "" });
  const allQuery = useGetChatConversationsInfiniteQuery({ tab: "all", archived: false, search: "" });
  const [openDirect, { isLoading: openingSelf }] = useOpenChatDirectMutation();

  const unreadChats = useMemo(
    () => unreadQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [unreadQuery.data],
  );
  const recentChats = useMemo(() => allQuery.data?.pages.flatMap((p) => p.data) ?? [], [allQuery.data]);
  const showingUnread = unreadChats.length > 0;
  const pickUp = (showingUnread ? unreadChats : recentChats).slice(0, PICK_UP_COUNT);
  const loading = unreadQuery.isLoading || (!showingUnread && allQuery.isLoading);

  const unreadCount = unread?.conversations ?? 0;
  const part = dayPart(new Date().getHours());

  const messageMyself = async () => {
    if (!me?.id) return;
    try {
      const c = await openDirect(me.id).unwrap();
      onOpen(c.id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.open")));
    }
  };

  const actions: { key: string; icon: LucideIcon; tone: string; label: string; hint: string; run: () => void; disabled?: boolean }[] = [
    {
      key: "chat",
      icon: MessageSquarePlus,
      tone: "bg-primary-muted text-primary",
      label: t("landing.actions.chat"),
      hint: t("landing.actions.chat_hint"),
      run: () => onNew("menu"),
    },
    {
      key: "group",
      icon: Users,
      tone: "bg-info-muted text-info",
      label: t("landing.actions.group"),
      hint: t("landing.actions.group_hint"),
      run: () => onNew("group"),
    },
    ...(canBroadcast
      ? [
          {
            key: "broadcast",
            icon: Megaphone,
            tone: "bg-warning-muted text-warning",
            label: t("landing.actions.broadcast"),
            hint: t("landing.actions.broadcast_hint"),
            run: () => onNew("broadcast"),
          },
          {
            key: "work-status",
            icon: Clock,
            tone: "bg-success-muted text-success",
            label: t("landing.actions.work_status"),
            hint: t("landing.actions.work_status_hint"),
            run: () => onNew("work-status"),
          },
        ]
      : []),
    {
      key: "self",
      icon: NotebookPen,
      tone: "bg-accent-muted text-accent-foreground",
      label: t("landing.actions.self"),
      hint: t("landing.actions.self_hint"),
      run: () => void messageMyself(),
      disabled: openingSelf || !me?.id,
    },
  ];

  const tips: { icon: LucideIcon; text: string }[] = [
    { icon: Bold, text: t("landing.tips.format") },
    { icon: PictureInPicture2, text: t("landing.tips.windows") },
    { icon: SquareKanban, text: t("landing.tips.tickets") },
  ];

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10">
        {/* Greeting */}
        <header className="flex flex-col gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {me?.firstName
              ? t(`landing.greeting_${part}`, { name: me.firstName })
              : t(`landing.greeting_${part}_anonymous`)}
          </h1>
          <p className="text-sm text-muted-foreground">
            {unreadCount > 0 ? t("landing.unread_summary", { count: unreadCount }) : t("landing.all_caught_up")}
          </p>
        </header>

        {/* Pick up where you left off */}
        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {showingUnread ? t("landing.waiting_on_you") : t("landing.recent")}
          </h2>
          {loading ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 2 }, (_, i) => (
                <Skeleton key={i} className="h-[76px] rounded-xl" />
              ))}
            </div>
          ) : pickUp.length ? (
            <ul className="grid gap-3 sm:grid-cols-2">
              {pickUp.map((c, i) => (
                <li
                  key={c.id}
                  className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-500 motion-reduce:animate-none"
                  style={{ animationDelay: `${80 + i * 60}ms` }}
                >
                  <ChatCard conversation={c} myUserId={me?.id} onOpen={() => onOpen(c.id)} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              {t("landing.no_chats")}
            </p>
          )}
        </section>

        {/* Start something */}
        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{t("landing.start")}</h2>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {actions.map(({ key, icon: Icon, tone, label, hint, run, disabled }) => (
              <li key={key}>
                <button
                  type="button"
                  onClick={run}
                  disabled={disabled}
                  className="group flex h-full w-full flex-col items-start gap-3 rounded-xl border border-border bg-surface p-4 text-start shadow-xs transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-60 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  <span
                    className={cn(
                      "flex size-10 items-center justify-center rounded-xl transition-transform group-hover:scale-110 motion-reduce:group-hover:scale-100",
                      tone,
                    )}
                  >
                    <Icon className="size-5" />
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground">{label}</span>
                    <span className="text-xs leading-snug text-muted-foreground">{hint}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* What chat can do */}
        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{t("landing.tips_title")}</h2>
          <ul className="flex flex-col gap-2">
            {tips.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 rounded-lg px-1 text-sm text-muted-foreground">
                <Icon className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

const ChatCard = ({
  conversation: c,
  myUserId,
  onOpen,
}: {
  conversation: IChatConversation;
  myUserId: number | undefined;
  onOpen: () => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const title = conversationTitle(t, c);
  const unread = c.unreadCount;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 rounded-xl border border-border bg-surface p-3.5 text-start shadow-xs transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <ChatAvatar
        name={title}
        imageUrl={c.type === "direct" ? c.peer?.profileImage : c.imageUrl}
        kind={c.type}
        channel={channelOf(c)}
        size="md"
        isOnline={c.type === "direct" && !c.isSelf && !!chatHub.getPresence(c.peer?.id)?.online}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className={cn("truncate text-sm text-foreground", unread > 0 ? "font-semibold" : "font-medium")}>
            {title}
          </span>
          <span className={cn("ms-auto shrink-0 text-[10px]", unread > 0 ? "font-semibold text-primary" : "text-muted-foreground")}>
            {conversationTime(t, formatDate, c.lastMessage?.createdAt ?? c.lastActivityAt)}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span dir="auto" className="truncate text-xs text-muted-foreground">
            {lastMessagePreview(t, c, myUserId)}
          </span>
          {unread > 0 ? (
            <span
              className="ms-auto flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground"
              aria-label={t("a11y.unread_count", { count: unread })}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          ) : (
            <ArrowRight className="ms-auto size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 rtl-flip" />
          )}
        </span>
      </span>
    </button>
  );
};
